import { Fragment } from "react";
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { Link, type LinkProps } from "@tanstack/react-router";

import { LogoutButton } from "@/components";
import { careplanQueryOptions } from "@/domains/careplan/factories/careplan-query-options";
import { useIsProgressEnabled } from "@/domains/progress/hooks/use-is-progress-enabled";
import { PROGRESS_NAV_LABEL } from "@/domains/progress/utils/progress-entry-points";
import { env } from "@/env";
import type { SVGProps } from "@/ui";
import { Button, focusRing, focusRingInset, icons } from "@/ui";
import { lockPageScroll, unlockPageScroll } from "@/utils";
import { ExtraNavBarItem } from "./extra-nav-bar-item";
import { NavBarItem } from "./nav-bar-item";
import { PendingActionBanner } from "./pending-action-banner";

type NavBarProps = {
  onToggleCart: () => void;
  showCart?: boolean;
};

const ProgressNavIcon = (props: SVGProps) => {
  return <icons.ChartBarIcon {...props} className="size-7" />;
};

type NavBarLink = {
  label: string;
} & Pick<LinkProps, "to" | "search">;

const links: NavBarLink[] = [
  { to: "/more", search: { tab: "basic-information" }, label: "My Account" },
  { to: "/more", search: { tab: "about-eo" }, label: "About EO" },
  { to: "/product-research-and-testing", label: "Research & Testing" },
  { to: "/clinical-team", label: "Clinical Team" },
  { to: "/cannabis-101", label: "Cannabis 101" },
  { to: "/faqs", label: "FAQs" },
];

const iconButton = `flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-content transition-colors hover:bg-surface-hover hover:text-accent sm:size-11 ${focusRing}`;

export const NavBar = ({ onToggleCart, showCart = true }: NavBarProps) => {
  const { data: cart } = useQuery({
    ...careplanQueryOptions.cart,
    enabled: showCart,
  });
  const isProgressEnabled = useIsProgressEnabled();

  const handleNavItemClick = (closeExtraItemsCallback: () => void) => {
    unlockPageScroll();
    closeExtraItemsCallback();
  };

  return (
    <Disclosure as="header" className="sticky top-0 z-20 flex flex-col bg-canvas">
      {({ close, open }) => {
        return (
          <Fragment>
            <div className="relative z-10 flex h-16 items-center gap-2 border-b border-line bg-surface px-3 sm:h-18 sm:gap-6 sm:px-8 lg:px-10">
              <Link
                className={`mr-auto flex h-full items-center rounded-md transition-opacity hover:opacity-80 ${focusRingInset}`}
                onClick={() => {
                  return handleNavItemClick(close);
                }}
                to="/"
              >
                <span className="sr-only">EO Care home</span>
                <icons.EOCareIcon
                  aria-hidden="true"
                  className="h-7 w-10 text-content sm:h-8 sm:w-12"
                />
              </Link>

              <nav
                aria-label="Main"
                className="flex h-full items-center gap-1 text-content sm:gap-4 lg:gap-8"
              >
                <p className="hidden text-content-muted lg:block">
                  <span className="font-semibold text-content-secondary">Call Us:</span>{" "}
                  {env.VITE_EO_CONTACT_NUMBER}
                </p>

                <NavBarItem
                  icon={icons.ClipboardIcon}
                  onClick={() => {
                    return handleNavItemClick(close);
                  }}
                  to="/careplan"
                >
                  Care Plan
                </NavBarItem>

                {isProgressEnabled ? (
                  <NavBarItem
                    icon={ProgressNavIcon}
                    onClick={() => {
                      return handleNavItemClick(close);
                    }}
                    to="/progress"
                  >
                    {PROGRESS_NAV_LABEL}
                  </NavBarItem>
                ) : null}

                <a className={`${iconButton} lg:hidden`} href={`tel:${env.VITE_EO_CONTACT_NUMBER}`}>
                  <span className="sr-only">{`Call us at ${env.VITE_EO_CONTACT_NUMBER}`}</span>
                  <icons.PhoneIcon aria-hidden="true" />
                </a>

                <NavBarItem
                  icon={icons.BubbleMessageIcon}
                  onClick={() => {
                    return handleNavItemClick(close);
                  }}
                  to="/messages"
                >
                  Message Us
                </NavBarItem>

                {showCart ? (
                  <Button
                    aria-label={cart?.count ? `View cart, ${cart.count} items` : "View cart"}
                    className={`gap-2 rounded-full px-1 font-normal hover:bg-surface-hover hover:text-accent sm:px-3 ${focusRing}`}
                    onClick={() => {
                      handleNavItemClick(close);
                      onToggleCart();
                    }}
                    size="tight"
                    type="button"
                    variant="focus"
                  >
                    <span className="hidden py-2 lg:block">View Cart</span>

                    <span className="relative flex size-10 items-center justify-center">
                      <icons.CartIcon aria-hidden="true" />

                      {cart?.count ? (
                        <span className="absolute top-1.5 right-1 flex size-4.5 items-center justify-center rounded-full bg-surface-inverted text-xs font-semibold text-content-on-inverted">
                          {cart.count}
                        </span>
                      ) : null}
                    </span>
                  </Button>
                ) : null}

                <DisclosureButton
                  className={iconButton}
                  onClick={() => {
                    return open ? unlockPageScroll() : lockPageScroll();
                  }}
                >
                  <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>

                  {open ? (
                    <icons.XMarkIcon aria-hidden="true" className="size-6" />
                  ) : (
                    <icons.HamburgerMenuIcon aria-hidden="true" className="size-5" />
                  )}
                </DisclosureButton>
              </nav>

              <Transition
                as={Fragment}
                enter="transition duration-100 ease-out"
                enterFrom="transform opacity-0"
                enterTo="transform opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform opacity-100"
                leaveTo="transform opacity-0"
              >
                <DisclosurePanel className="fixed inset-x-0 top-16 bottom-0 flex flex-col justify-between gap-6 overflow-y-auto bg-surface px-5 py-8 sm:top-18 sm:px-8">
                  <ul>
                    {links.map((link) => {
                      return (
                        <ExtraNavBarItem
                          key={link.to + JSON.stringify(link.search ?? {})}
                          onClick={() => {
                            return handleNavItemClick(close);
                          }}
                          {...link}
                        >
                          {link.label}
                        </ExtraNavBarItem>
                      );
                    })}
                  </ul>

                  <LogoutButton className="w-fit" />
                </DisclosurePanel>
              </Transition>
            </div>

            <PendingActionBanner />
          </Fragment>
        );
      }}
    </Disclosure>
  );
};
